type SiteNavigationSchemaLink = {
  name: string
  url: string
}

type FaqPageSchemaQuestion = {
  question: string
  answer: string
}

export const useSchemaOrg = () => {
  const { locale, t } = useNuxtApp().$i18n
  const { localizeURL } = useSiteUrl()
  const siteNavigationLinks = useState<SiteNavigationSchemaLink[]>('schema-org-navigation-links', () => [])
  const faqPageSchemaQuestions = useState<FaqPageSchemaQuestion[]>('schema-org-faq-page-questions', () => [])
  const isFaqPageSchemaInitialized = useState('schema-org-faq-page-initialized', () => false)
  const getBaseUrl = () => {
    const domain = localeConfig[locale.value]?.domain || 'www.michaelhill.com.au'
    return `https://${domain}`
  }

  const normalizeSiteNavigationUrl = (url: string, baseUrl: string) => {
    const trimmedUrl = url?.trim()

    if (!trimmedUrl || /^(#|javascript:|mailto:|tel:)/i.test(trimmedUrl)) {
      return null
    }

    if (trimmedUrl.startsWith('http')) {
      return trimmedUrl
    }

    return `${baseUrl}${localizeURL(trimmedUrl)}`
  }

  const sanitizeSiteNavigationLinks = (links: SiteNavigationSchemaLink[]) => {
    const baseUrl = getBaseUrl()

    return links.reduce<SiteNavigationSchemaLink[]>((acc, link) => {
      const name = link.name?.trim()
      const normalizedUrl = normalizeSiteNavigationUrl(link.url, baseUrl)

      if (!name || !normalizedUrl) {
        return acc
      }

      const dedupeKey = `${name}::${normalizedUrl}`
      if (acc.some((item) => `${item.name}::${item.url}` === dedupeKey)) {
        return acc
      }

      acc.push({
        name,
        url: normalizedUrl,
      })

      return acc
    }, [])
  }

  const navigationSchemaLinks = computed(() => sanitizeSiteNavigationLinks(siteNavigationLinks.value))

  // SSR-safe current URL resolution: useRequestURL > window.location
  const getCurrentUrl = (baseUrl: string): string => {
    const origin = resolveRequestOrigin()
    if (origin) {
      try {
        return useRequestURL().href
      } catch {
        return import.meta.client ? window.location.href : baseUrl
      }
    }
    return baseUrl
  }

  const socialLinks = [
    'https://www.instagram.com/michaelhillj/?hl=en',
    'https://www.facebook.com/MichaelHillJ/',
    'https://www.tiktok.com/@michaelhillfinejewellers',
    'https://www.pinterest.com.au/michaelhillj/',
    'https://www.linkedin.com/company/michael-hill',
    'https://www.youtube.com/c/michaelhill',
  ]

  const init = () => {
    useHead(() => {
      const baseUrl = getBaseUrl()
      const logoUrl = `${baseUrl}/mhj-logo.svg`

      // NOTE: Update here if contact details change
      const contactInfo = {
        AU: { email: 'online@michaelhill.com.au', telephone: '+61 1800 445 590' },
        CA: { email: 'online@michaelhill.ca', telephone: '+1 855 354 4455' },
        NZ: { email: 'online@michaelhill.co.nz', telephone: '+64 800 445 590' },
      } satisfies Record<string, { email: string; telephone: string }>
      const { email, telephone } = contactInfo[localeConfig[locale.value].country || 'AU']

      const orgSchema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${baseUrl}/#organization`,
        name: 'Michael Hill',
        description: t('organization_schema.description'),
        logo: logoUrl,
        url: baseUrl,
        sameAs: socialLinks,
        location: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '34 Southgate Avenue',
            addressLocality: 'Cannon Hill',
            addressRegion: 'QLD',
            postalCode: '4170',
            addressCountry: 'Australia',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: -27.465442,
            longitude: 153.09341,
          },
        },
        contactPoint: [
          { '@type': 'ContactPoint', email, contactType: 'General Enquiry' },
          { '@type': 'ContactPoint', telephone, contactType: 'General Enquiry' },
        ],
      }

      return {
        script: [{ key: 'org-schema', type: 'application/ld+json', innerHTML: JSON.stringify(orgSchema) }],
      }
    })
  }

  const initSiteNavigationSchema = () => {
    useHead(() => {
      if (!navigationSchemaLinks.value.length) {
        return {
          script: [],
        }
      }

      const navigationSchema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: navigationSchemaLinks.value.map((link) => ({
          '@type': 'SiteNavigationElement',
          name: link.name,
          url: link.url,
        })),
      }

      return {
        script: [
          { key: 'site-navigation-schema', type: 'application/ld+json', innerHTML: JSON.stringify(navigationSchema) },
        ],
      }
    })
  }

  const setSiteNavigationLinks = (links: SiteNavigationSchemaLink[]) => {
    siteNavigationLinks.value = links
  }

  const registerSiteNavigationLink = (link: SiteNavigationSchemaLink) => {
    siteNavigationLinks.value.push(link)
  }

  const initFaqPageSchema = () => {
    if (isFaqPageSchemaInitialized.value) return

    isFaqPageSchemaInitialized.value = true

    useHead(() => {
      if (!faqPageSchemaQuestions.value.length) {
        return {
          script: [],
        }
      }

      const faqPageSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqPageSchemaQuestions.value.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      }

      return {
        script: [{ key: 'faq-page-schema', type: 'application/ld+json', innerHTML: JSON.stringify(faqPageSchema) }],
      }
    })
  }

  const registerFaqPageQuestions = (questions: FaqPageSchemaQuestion[]) => {
    initFaqPageSchema()
    faqPageSchemaQuestions.value.push(...questions)
  }

  const initProductSchema = (product: FlattenedProduct | null, masterProduct: FlattenedProduct | null) => {
    if (!product) return

    const defaultLocale = localeConfig[locale.value]
    const baseUrl = getBaseUrl()
    const currentUrl = getCurrentUrl(baseUrl)

    // Get review data from master product
    const reviewAverage = parseFloat(masterProduct?.c_bvAverageRating || '0')
    const reviewCount = parseInt(masterProduct?.c_bvReviewCount || '0', 10)

    const productImage = findPrimaryAngle(product.images) || ''

    // additionalProperties come from pdpPreferences product details attributes
    const preferencesStore = usePreferencesStore()
    const additionalProperties: Array<{ '@type': 'PropertyValue'; name: string; value: string }> = []

    preferencesStore.pdp?.productDetailsAttribute?.forEach((attr: any) => {
      const referenceIdentifier = attr.fields?.referenceIdentifier
      const title = attr.fields?.title
      if (referenceIdentifier && title && product[referenceIdentifier]) {
        additionalProperties.push({
          '@type': 'PropertyValue',
          name: title,
          value: product[referenceIdentifier],
        })
      }
    })

    const aggregateRating =
      reviewAverage > 0 && reviewCount > 0
        ? { '@type': 'AggregateRating', ratingValue: reviewAverage.toFixed(1), reviewCount }
        : undefined

    const availability = product.inventory?.ats > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'

    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': `${currentUrl}#product`,
      url: currentUrl,
      name: product.name || '',
      description: product.shortDescription || product.longDescription || '',
      image: productImage,
      sku: product.id || '',
      mpn: product.id || '',
      brand: { '@type': 'Brand', name: 'Michael Hill' },
      category: 'https://schema.org/JewelryStore',
      material: product.c_metalContent,
      color: product.c_metalColour,
      size: 'Select Size',
      ...(additionalProperties.length > 0 && { additionalProperty: additionalProperties }),
      ...(aggregateRating && { aggregateRating }),
      offers: {
        '@type': 'Offer',
        url: currentUrl,
        price: product.price || 0,
        priceCurrency: product.currency || 'AUD',
        availability,
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@type': 'Organization', name: 'Michael Hill' },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: defaultLocale.country || 'AU',
          },
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          merchantReturnDays: 30,
          returnFees: 'https://schema.org/FreeReturn',
          returnMethod: 'https://schema.org/ReturnByMail',
          applicableCountry: defaultLocale.country || 'AU',
        },
      },
    }

    useHead({
      script: [{ key: 'product-schema', type: 'application/ld+json', innerHTML: JSON.stringify(productSchema) }],
    })
  }

  const initPlpSchema = (name: string, products: Omit<ProductTileProps, 'index'>[]) => {
    if (!products.length) return

    const baseUrl = getBaseUrl()
    const currentUrl = getCurrentUrl(baseUrl)

    // No offers on tiles: price/availability makes Google assess each tile as a merchant listing.
    const itemListElement = products.map((product, index) => {
      const productUrl = product.pdpUrl?.startsWith('http') ? product.pdpUrl : `${baseUrl}${product.pdpUrl}`

      return {
        '@type': 'ListItem',
        position: index + 1,
        url: productUrl,
        item: {
          '@type': 'Product',
          name: product.name || '',
          image: product.defaultImage?.src || '',
          url: productUrl,
        },
      }
    })

    const plpSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name,
      url: currentUrl,
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      // Tiles marked up so far, not the category total.
      numberOfItems: itemListElement.length,
      itemListElement,
    }

    useHead({
      script: [{ key: 'plp-schema', type: 'application/ld+json', innerHTML: JSON.stringify(plpSchema) }],
    })
  }

  type ContentSchemaData = {
    type: 'Article' | 'BlogPosting'
    headline: string
    datePublished?: string
    dateModified?: string
    authorName?: string
    description?: string
    articleBody?: string
    image?: string
  }

  const initContentSchema = (data: ContentSchemaData) => {
    const baseUrl = getBaseUrl()
    const currentUrl = getCurrentUrl(baseUrl)
    const logoUrl = `${baseUrl}/mhj-logo.svg`
    const orgName = 'Michael Hill Australia'
    const isBlog = data.type === 'BlogPosting'

    const schema = {
      '@context': 'https://schema.org',
      '@type': data.type,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': currentUrl,
      },
      headline: data.headline,
      ...(data.description && { description: data.description }),
      ...(data.articleBody && { articleBody: data.articleBody }),
      ...(data.image && { image: data.image }),
      author: data.authorName
        ? { '@type': 'Person', name: data.authorName }
        : { '@type': 'Organization', name: orgName },
      publisher: {
        '@type': 'Organization',
        name: orgName,
        logo: {
          '@type': 'ImageObject',
          url: logoUrl,
        },
      },
      ...(data.datePublished && { datePublished: data.datePublished }),
      ...(data.dateModified && { dateModified: data.dateModified }),
      ...(isBlog && { url: currentUrl }),
      ...(isBlog && {
        isPartOf: {
          '@type': 'Blog',
          name: 'Connected',
          url: `${baseUrl}${localizeURL('/connected')}`,
        },
      }),
    }

    useHead({
      script: [{ key: 'content-schema', type: 'application/ld+json', innerHTML: JSON.stringify(schema) }],
    })
  }

  /**
   * Converts time string from SFCC format to hh:mm:ss 24-hour format
   */
  const formatTimeTo24h = (time: string | undefined): string | null => {
    if (!time) return null
    const match = time.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
    if (!match) return null
    let hours = parseInt(match[1], 10)
    const minutes = match[2]
    const period = match[3].toUpperCase()
    if (period === 'PM' && hours !== 12) hours += 12
    if (period === 'AM' && hours === 12) hours = 0
    return `${String(hours).padStart(2, '0')}:${minutes}:00`
  }

  /**
   * Builds the openingHoursSpecification array from SFCC c_storeHours{Day}Start/End fields.
   */
  const buildOpeningHours = (store: SFCCStore) => {
    const groupMap = new Map<string, string[]>()
    for (const day of DAYS) {
      // SAFETY: c_storeHours{Weekday}Start/End are string fields; a weekday with no matching key reads undefined.
      const opens = formatTimeTo24h(store[`c_storeHours${day}Start` as keyof SFCCStore] as string | undefined)
      // SAFETY: as above.
      const closes = formatTimeTo24h(store[`c_storeHours${day}End` as keyof SFCCStore] as string | undefined)
      if (!opens || !closes) continue
      const key = `${opens}|${closes}`
      if (!groupMap.has(key)) groupMap.set(key, [])
      groupMap.get(key)!.push(day)
    }

    return Array.from(groupMap.entries()).map(([key, dayList]) => {
      const [opens, closes] = key.split('|')
      return {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: dayList.length === 1 ? dayList[0] : dayList,
        opens,
        closes,
      }
    })
  }

  /**
   * geoRadius is fixed at "10" for all stores.
   *   spec defines 10km for metro and 20km for regional stores.
   *   Update once SFCC gets a metro/regional flag on the store record.
   */
  const initLocalBusinessSchema = (store: Ref<SFCCStore | undefined>) => {
    useHead(() => {
      if (!store.value) return { script: [] }

      const baseUrl = getBaseUrl()
      const currentUrl = getCurrentUrl(baseUrl)
      const streetAddress = [store.value.address1, store.value.address2].filter(Boolean).join(', ')

      const logoUrl = `${baseUrl}/mhj-logo.svg`

      const localBusinessSchema = {
        '@context': 'https://schema.org',
        '@type': 'JewelryStore',
        name: store.value.name || '',
        url: currentUrl,
        ...(store.value.phone && { telephone: store.value.phone }),
        image: logoUrl,
        logo: { '@type': 'ImageObject', url: logoUrl },
        sameAs: socialLinks,
        address: {
          '@type': 'PostalAddress',
          streetAddress,
          addressLocality: store.value.city || '',
          addressRegion: store.value.stateCode || '',
          postalCode: store.value.postalCode || '',
          addressCountry: store.value.countryCode || '',
        },
        ...(store.value.latitude &&
          store.value.longitude && {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: store.value.latitude.toFixed(7),
              longitude: store.value.longitude.toFixed(7),
            },
          }),
        openingHoursSpecification: buildOpeningHours(store.value),
        areaServed: {
          '@type': 'GeoCircle',
          address: {
            '@type': 'PostalAddress',
            streetAddress,
            addressLocality: store.value.city || '',
            addressRegion: store.value.stateCode || '',
            postalCode: store.value.postalCode || '',
            addressCountry: store.value.countryCode || '',
          },
          geoRadius: '10',
        },
      }

      return {
        script: [
          { key: 'local-business-schema', type: 'application/ld+json', innerHTML: JSON.stringify(localBusinessSchema) },
        ],
      }
    })
  }

  const initBlogSchema = (data: Omit<ContentSchemaData, 'type'>) => initContentSchema({ ...data, type: 'BlogPosting' })

  return {
    init,
    initBlogSchema,
    initContentSchema,
    initFaqPageSchema,
    initLocalBusinessSchema,
    initProductSchema,
    initPlpSchema,
    registerFaqPageQuestions,
    initSiteNavigationSchema,
    registerSiteNavigationLink,
    setSiteNavigationLinks,
  }
}
